import React, { useState } from 'react';
import { motion } from 'motion/react';
import { ShieldCheck, Plus, ShoppingCart, DollarSign, ListFilter, HelpCircle, Laptop, Coins, Bookmark, Sparkles, Send, Receipt, LayoutGrid, Cpu, Radio } from 'lucide-react';
import { NexusUser } from '../types/nexus';
import { NexoraXSortDropdown } from './NexoraXSortDropdown';
import { NexusSmartGadgetMarketplace } from './NexusSmartGadgetMarketplace';
import { NexusTechUpdateRadar } from './NexusTechUpdateRadar';
import { NexusDigitalProductMarketplace } from './NexusDigitalProductMarketplace';


interface NexusMarketplaceProps {
  user: NexusUser;
  isAdmin?: boolean;
  onPurchaseCredits: (credits: number, cost: number) => void;
  onUpgradePlan: (plan: 'Bronze' | 'Silver' | 'Gold' | 'Platinum') => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddTaskToWorkSuite?: (title: string, priority: 'URGENT' | 'HIGH' | 'MEDIUM' | 'LOW') => void;
}

export const NexusMarketplace: React.FC<NexusMarketplaceProps> = ({
  user,
  isAdmin = false,
  onPurchaseCredits,
  onUpgradePlan,
  onAddLog,
  onAddTaskToWorkSuite
}) => {
  const [activeSubTab, setActiveSubTab] = useState<'saas' | 'smart_gadgets' | 'tech_update_radar'>('smart_gadgets');
  const [filter, setFilter] = useState('ALL');
  const [sortBy, setSortBy] = useState<'priority' | 'name' | 'lastModified'>('priority');

  const categories = ['ALL', 'Search Credits', 'Hosting Packages', 'AI Agent Templates', 'WorkSuite Add-ons', 'Advanced Analytics'];

  const products = [
    // Credits
    {
      id: 'cr-1',
      name: 'Search Credit Pack: Starter',
      category: 'Search Credits',
      unlocks: 'Unlocks 500 high-speed meta-queries with caching metrics.',
      price: 5.00,
      includedUsage: '500 search queries (1p per query)',
      requiredPlan: 'None (Bronze compatible)',
      type: 'credits',
      amount: 500,
      priority: 'Medium',
      lastModified: Date.now() - 3600000
    },
    {
      id: 'cr-2',
      name: 'Search Credit Pack: Professional',
      category: 'Search Credits',
      unlocks: 'Unlocks 1,000 deep sector competitor queries.',
      price: 10.00,
      includedUsage: '1,000 search queries (1p per query)',
      requiredPlan: 'None (Bronze compatible)',
      type: 'credits',
      amount: 1000,
      priority: 'High',
      lastModified: Date.now() - 7200000
    },
    {
      id: 'cr-3',
      name: 'Search Credit Pack: Enterprise Scale',
      category: 'Search Credits',
      unlocks: 'Unlocks 2,500 priority searches on Azure Bing grounding API.',
      price: 25.00,
      includedUsage: '2,500 search queries (1p per query)',
      requiredPlan: 'None (Silver+ recommended)',
      type: 'credits',
      amount: 2500,
      priority: 'High',
      lastModified: Date.now() - 18000000
    },
    // Hosting
    {
      id: 'ho-1',
      name: 'Managed Hosting',
      category: 'Hosting Packages',
      unlocks: 'Managed setup with automated SSL security protection and secure hosting configuration.',
      price: 29.00,
      includedUsage: '10GB storage, 100GB fast-bandwidth pipelines',
      requiredPlan: 'Silver+',
      type: 'hosting',
      priority: 'Low',
      lastModified: Date.now() - 86400000
    },
    // AI Agents
    {
      id: 'ag-1',
      name: 'Lead Prospector Node Agent',
      category: 'AI Agent Templates',
      unlocks: 'Deploys localized search engine crawler agents to find digital-starved tax targets & generate Outreach pitches.',
      price: 49.00,
      includedUsage: 'Unlimited pipeline configurations, 10 concurrent tasks',
      requiredPlan: 'Gold+',
      type: 'agent',
      priority: 'Core',
      lastModified: Date.now() - 1000000
    },
    {
      id: 'ag-2',
      name: 'Social Auto-Publish Post Script Agent',
      category: 'AI Agent Templates',
      unlocks: 'Deploys scheduler agents linked into Media Studio rendering tools to completely run automated social media feeds.',
      price: 39.00,
      includedUsage: 'Complete scheduler queue, 5 social channel links',
      requiredPlan: 'Gold+',
      type: 'agent',
      priority: 'Medium',
      lastModified: Date.now() - 500000
    },
    // WorkSuite
    {
      id: 'ws-1',
      name: 'WorkSuite Lite Project Manager',
      category: 'WorkSuite Add-ons',
      unlocks: 'Adds document editors, Chronos spreadsheet modules, and team approval gates inside productivity dashboards.',
      price: 19.00,
      includedUsage: 'Unlimited tasks, meetings summaries & CRM contacts',
      requiredPlan: 'Bronze+',
      type: 'worksuite',
      priority: 'Medium',
      lastModified: Date.now() - 25000000
    }
  ];

  const filteredProducts = filter === 'ALL'
    ? products
    : products.filter(p => p.category === filter);

  // Sorting logic
  const priorityOrder: Record<string, number> = {
    'Core': 5,
    'High': 4,
    'Medium': 3,
    'Low': 2,
    'Archived': 1
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    } else if (sortBy === 'lastModified') {
      const aTime = a.lastModified || 0;
      const bTime = b.lastModified || 0;
      return bTime - aTime;
    } else if (sortBy === 'priority') {
      const aPri = priorityOrder[a.priority || 'Medium'] || 3;
      const bPri = priorityOrder[b.priority || 'Medium'] || 3;
      if (aPri !== bPri) {
        return bPri - aPri; // Higher numerical score first
      }
      return a.name.localeCompare(b.name);
    }
    return 0;
  });

  const handlePurchase = (p: typeof products[0]) => {
    onAddLog('SaaS Marketplace', `Purchase request: "${p.name}"`, 'PENDING_APPROVAL', `Secure payment gateway callback initiated for value £${p.price.toFixed(2)}.`);
    
    // Simulate payment options
    const confirmPayment = window.confirm(`SECURE CHECKOUT\n\nProduct: ${p.name}\nPrice: £${p.price.toFixed(2)}\n\nDo you authorize payment of £${p.price.toFixed(2)} via Safe Stripe provider?`);
    
    if (confirmPayment) {
      if (p.type === 'credits') {
        onPurchaseCredits(p.amount || 0, p.price);
        onAddLog('SaaS Marketplace', `Purchase success: "${p.name}"`, 'SUCCESS', `Credited ${p.amount} search queries to balance. Transaction verified.`);
        alert(`SUCCESS! Your account is credited with ${p.amount} search credits!`);
      } else {
        onAddLog('SaaS Marketplace', `License activated: "${p.name}"`, 'SUCCESS', `Unlocked premium ${p.category} module on WorkspaceID: ns-7729.`);
        alert(`SUCCESS! Product "${p.name}" unlocked. This capability is added to your control plane.`);
      }
    } else {
      onAddLog('SaaS Marketplace', `Purchase cancelled: "${p.name}"`, 'WARNING', 'Checkout transaction terminated or aborted by user.');
    }
  };

  const handleInvoiceRequest = (p: typeof products[0]) => {
    onAddLog('SaaS Marketplace', `Invoice Requested: "${p.name}"`, 'PENDING_APPROVAL', `Manual B2B invoice generation request queued for £${p.price.toFixed(2)}. Outbound email link dispatched to support@nexoraxdigital.co.uk.`);
    alert(`INVOICE REQUEST SENT\n\nAdministrative task logged. A custom digital invoice for £${p.price.toFixed(2)} (with 20% VAT accounting breakout) has been queued. Direct bank settlement available via invoice instructions within 24 hours.`);
  };

  return (
    <div className="w-full space-y-6">
      {/* Decoupled Cross-Reference Banner to Digital Products */}
      <div className="p-4 rounded-xl border border-purple-500/30 bg-gradient-to-r from-purple-950/20 via-black to-zinc-950 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-[0_0_15px_rgba(232,64,243,0.06)]">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-[#e840f3]/10 border border-[#e840f3]/25">
            <Sparkles className="w-4 h-4 text-[#e840f3] animate-pulse" />
          </div>
          <div>
            <h4 className="text-xs font-sans font-black text-white uppercase tracking-wider">
              Looking for Ready-Made Websites, Apps or AI Workflows?
            </h4>
            <p className="text-[10px] font-sans text-slate-400 mt-0.5">
              We have launched a dedicated top-level store for templates, app files, Python packs, and n8n/Make automation flows.
            </p>
          </div>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('nexorax-trigger-tab-change', { detail: 'Digital Products' }))}
          className="px-4 py-2 bg-[#e840f3]/10 hover:bg-[#e840f3]/25 border border-[#e840f3]/40 text-[#e840f3] hover:text-white font-mono text-[10px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 whitespace-nowrap shadow-[0_0_10px_rgba(232,64,243,0.15)] cursor-pointer"
        >
          Go to Digital Products →
        </button>
      </div>

      {/* Dynamic SubTab Navigation Switcher */}
      <div className="flex flex-wrap border-b border-zinc-900/60 pb-3 gap-3 text-[10px] font-mono font-black uppercase tracking-widest">
        <button
          id="tab-tech-radar"
          onClick={() => setActiveSubTab('tech_update_radar')}
          className={`py-2 px-4 rounded-xl cursor-pointer transition-all flex items-center gap-2 ${
            activeSubTab === 'tech_update_radar'
              ? 'bg-[#00ffff]/15 text-[#00ffff] border border-[#00ffff]/35 shadow-[0_0_15px_rgba(0,255,255,0.1)]'
              : 'text-slate-500 hover:text-slate-300 bg-black/30 border border-zinc-900/50'
          }`}
        >
          <Radio className="w-3.5 h-3.5 animate-pulse text-[#00ffff]" />
          Nexus Tech Update Radar
        </button>
        <button
          id="tab-smart-gadgets"
          onClick={() => setActiveSubTab('smart_gadgets')}
          className={`py-2 px-4 rounded-xl cursor-pointer transition-all flex items-center gap-2 ${
            activeSubTab === 'smart_gadgets'
              ? 'bg-[#e840f3]/15 text-[#e840f3] border border-[#e840f3]/35'
              : 'text-slate-500 hover:text-slate-300 bg-black/30 border border-zinc-900/50'
          }`}
        >
          <Cpu className="w-3.5 h-3.5" />
          Future Gadgets &amp; Smart Tech
        </button>
        <button
          id="tab-saas-credits"
          onClick={() => setActiveSubTab('saas')}
          className={`py-2 px-4 rounded-xl cursor-pointer transition-all flex items-center gap-2 ${
            activeSubTab === 'saas'
              ? 'bg-purple-900/40 text-pink-400 border border-purple-500/35'
              : 'text-slate-500 hover:text-slate-300 bg-black/30 border border-zinc-900/50'
          }`}
        >
          <LayoutGrid className="w-3.5 h-3.5" />
          SaaS Modules &amp; Credits
        </button>
      </div>

      {activeSubTab === 'tech_update_radar' && (
        <NexusTechUpdateRadar
          isAdmin={isAdmin}
          onAddLog={onAddLog}
          onAddWorkSuiteTask={(taskObj) => {
            if (onAddTaskToWorkSuite) {
               onAddTaskToWorkSuite(taskObj.title, taskObj.priority);
            }
          }}
        />
      )}

      {activeSubTab === 'smart_gadgets' && (
        <NexusSmartGadgetMarketplace isAdmin={isAdmin} onAddLog={onAddLog} />
      )}

      {activeSubTab === 'saas' && (
        <div className="space-y-6">
          {/* Marketplace Header */}
          <div className="glass-panel border border-[#e840f3]/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/10 via-black/40 to-cyan-950/10">
            <h2 className="text-2xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-[#e840f3]" />
              Nexus Product Marketplace
            </h2>
            <p className="text-xs font-mono text-pink-400/80 uppercase tracking-widest mt-1">
              Acquire elite product modules, templates, search packs, and system automation add-ons.
            </p>
          </div>

          {/* Filter Row and Sort dropdown */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-zinc-900 pb-4">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`py-1.5 px-3 rounded-lg font-mono text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                    filter === cat
                      ? 'bg-[#e840f3]/15 text-[#e840f3] border-[#e840f3]/30 shadow-[0_0_10px_rgba(232,64,243,0.15)]'
                      : 'bg-transparent text-slate-500 border-zinc-900 hover:text-slate-350'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
            
            <NexoraXSortDropdown
              options={[
                { value: 'priority', label: 'Node Priority' },
                { value: 'name', label: 'Name' },
                { value: 'lastModified', label: 'Last Modified' }
              ]}
              selectedValue={sortBy}
              onSelect={(val) => setSortBy(val as 'priority' | 'name' | 'lastModified')}
            />
          </div>

          {/* Product Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedProducts.map((p) => (
              <div
                key={p.id}
                className="glass-panel border border-zinc-850 hover:border-[#e840f3]/25 hover:shadow-[0_0_20px_rgba(232,64,243,0.06)] rounded-2xl p-5 bg-black/30 flex flex-col justify-between transition-all duration-300 relative group"
              >
                {/* Holographic glowing borders */}
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#e840f3]/5 via-[#00ffff]/10 to-[#e840f3]/5 opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="space-y-4">
                  <div className="flex justify-between items-center border-b border-zinc-900 pb-2 mb-1">
                    <span className="text-[8px] font-mono font-black tracking-widest text-[#e840f3] uppercase bg-[#e840f3]/10 px-2 py-0.5 rounded">
                      {p.category}
                    </span>
                    <span className="text-[8px] font-mono text-slate-500 font-extrabold uppercase">
                      PLAN: {p.requiredPlan}
                    </span>
                  </div>

                  <div>
                    <h4 className="text-xs font-sans font-black uppercase text-white group-hover:text-[#e840f3] transition-colors tracking-tight">
                      {p.name}
                    </h4>
                    <p className="text-[10.5px] text-slate-400 font-sans leading-normal normal-case pt-2 font-medium">
                      {p.unlocks}
                    </p>
                  </div>

                  <div className="bg-black/40 border border-zinc-900 p-3 rounded-xl space-y-1 text-[9px] font-mono">
                    <div className="text-slate-500 uppercase">INCLUDED USAGE CAPACITY:</div>
                    <div className="text-white font-extrabold">{p.includedUsage}</div>
                  </div>
                </div>

                <div className="pt-5 mt-4 border-t border-zinc-900/60 space-y-3">
                  <div className="flex justify-between items-baseline font-mono">
                    <span className="text-[9px] text-slate-500 uppercase font-black">RETAIL PRICE:</span>
                    <span className="text-xl font-bold text-white tracking-tight">
                      £{p.price.toFixed(2)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[9px] font-mono font-black uppercase">
                    <button
                      onClick={() => handlePurchase(p)}
                      className="py-2.5 bg-gradient-to-r from-purple-600/30 to-[#e840f3]/30 hover:from-purple-600/50 hover:to-[#e840f3]/50 text-white border border-[#e840f3]/40 rounded-xl cursor-pointer text-center text-[9px] font-mono font-black tracking-wide"
                    >
                      Secure Checkout
                    </button>
                    <button
                      onClick={() => handleInvoiceRequest(p)}
                      className="py-2.5 bg-zinc-950 text-slate-350 hover:bg-zinc-900 border border-zinc-800 rounded-xl cursor-pointer text-center text-[9px] font-mono font-black tracking-wide flex items-center justify-center gap-1"
                    >
                      <Receipt size={11} className="text-slate-450" />
                      Req Invoice
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
